import { defineComponent, h, ref, watch } from 'vue';
import { Dialog, utility } from '@phi/components';
import IconExplanationFormSelect from '../IconExplanationFormSelect';
import { FormNumber, FormText } from '@components/form';
import { set } from '@phi/util/vuetils';
import { step_file_icon, stl_file_icon } from '@components/icons';
import { saveStlTextAsFile } from '@frontend/input_output';
import { export_as_step_to_download } from '@phi/export/frontend';
import { withSpinner } from '@frontend/state/spinner';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import { fold } from '@vaaas/fpts/result';
import { frontend_service_container } from '@phi/frontend/service-container';

export default defineComponent({
    props: {
        show: { required: true, type: Boolean},
        current_filename: { required: false, type: String, default: 'untitled' }
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    setup(props, { emit }) {
        // types
        type ExportOption = 'stl' | 'step';

        // components
        const SelectExportOption = IconExplanationFormSelect<ExportOption>();

        // data
        const export_option = ref<ExportOption>('stl');
        const model_name = ref(props.current_filename || 'Untitled');
        const t = (frontend_service_container().subscriptions.state.subscription_status.value === 'free') ? 300 : 500;
        const triangles_per_face = ref(t);

        // methods
        function reset() {
            triangles_per_face.value = 500;
            if (frontend_service_container().subscriptions.state.subscription_status.value === 'free')
                triangles_per_face.value = 300;
            model_name.value = props.current_filename || 'Untitled';
            export_option.value = 'stl';
        }

        async function on_export() {
            emit('update:show', false);

            switch (export_option.value) {
                case 'stl':
                    return saveStlTextAsFile(triangles_per_face.value, model_name.value);

                case 'step':
                    return withSpinner('Exporting as STEP...', export_as_step_to_download)(model_name.value).then(fold(
                        () => display_message_to_user(LOG.WARN, 'Failed to save STEP model.'),
                        () => display_message_to_user(LOG.INFO, 'Your file has been saved in your Downloads folder.'),
                    ));
                default:
                    return;
            }
        }

        // watchers
        watch(
            () => props.show,
            (value) => {
                if (value) reset();
            },
        );

        // render
        return () => h(Dialog, {
            show: props.show,
            'onUpdate:show': x => emit('update:show', x),
            onClose: () => emit('update:show', false),
            onConfirm: on_export,
            close_label: 'Cancel',
            confirm_label: 'Export',
            id: 'ExportModal',
            title: 'Export model',
            width: 'narrower',
        }, () => h('div', { class: utility.margin_gap_5 }, [
            h(SelectExportOption, {
                value: export_option.value,
                'onUpdate:value': set(export_option),
                options: [
                    {
                        value: 'stl',
                        icon: h(stl_file_icon),
                        title: 'Export as STL file',
                        subtitle: 'Save your model to your computer in the STL file format.',
                        disabled: false,
                    },
                    {
                        value: 'step',
                        icon: h(step_file_icon),
                        title: 'Export as STEP file',
                        subtitle: 'Save your model to your computer in the STEP file format.',
                        disabled: false,
                    },
                ],
            }),

            h('fieldset', [
                h('label', 'Model name'),
                h(FormText, {
                    value: model_name.value,
                    'onUpdate:value': set(model_name),
                }),
            ]),

            export_option.value === 'stl' && h('fieldset', [
                h('label', { id: 'stl_triangles_per_face' },
                    frontend_service_container().subscriptions.state.subscription_status.value === 'free' ?
                        'Triangles per face (20 - 300). For higher resolution please upgrade to Pro plan.' :
                        'Triangles per face (20 - 20000)'),
                h(FormNumber, {
                    value: triangles_per_face.value,
                    'onUpdate:value': set(triangles_per_face),
                    min: 20,
                    max: 20000,
                }),
            ]),
        ]));
    },
});
